import * as React from 'react';
import type { StoryMeta } from '@features/devtools/registry/types';
import { CommandBar } from './CommandBar';
import { useCanvasStore } from '@features/flows/state/canvas-store';

type Props = {
  openAt?: { x: number; y: number };
  preset?: 'basic' | 'mixed';
  mode?: 'pane' | 'connect';
};

function seedPalette(preset: Props['preset'] = 'basic') {
  const items = preset === 'mixed'
    ? [
        {
          key: 'math/add',
          title: 'Add Numbers',
          category: 'Math',
          ports: { data: { in: [{ id: 'a', schema: { type: 'number' } }, { id: 'b', schema: { type: 'number' } }], out: [{ id: 'sum', schema: { type: 'number' } }] } },
        },
        {
          key: 'text/concat',
          title: 'Concat',
          category: 'Text',
          ports: { data: { in: [{ id: 's1', schema: { type: 'string' } }, { id: 's2', schema: { type: 'string' } }], out: [{ id: 'out', schema: { type: 'string' } }] } },
        },
        {
          key: 'control/if',
          title: 'If',
          category: 'Control',
          ports: { data: { in: [{ id: 'cond', schema: { type: 'boolean' } }], out: [{ id: 'result', schema: {} }] } },
        },
        {
          key: 'json/parse',
          title: 'JSON Parse',
          category: 'JSON',
          ports: { data: { in: [{ id: 'text', schema: { type: 'string' } }], out: [{ id: 'value', schema: { type: 'object' } }] } },
        },
      ]
    : [
        {
          key: 'math/add',
          title: 'Add Numbers',
          category: 'Math',
          ports: { data: { in: [{ id: 'a', schema: { type: 'number' } }, { id: 'b', schema: { type: 'number' } }], out: [{ id: 'sum', schema: { type: 'number' } }] } },
        },
        {
          key: 'text/concat',
          title: 'Concat',
          category: 'Text',
          ports: { data: { in: [{ id: 's1', schema: { type: 'string' } }, { id: 's2', schema: { type: 'string' } }], out: [{ id: 'out', schema: { type: 'string' } }] } },
        },
      ];
  try {
    localStorage.setItem('palette.cache.v1', JSON.stringify({ items }));
  } catch {}
}

function CommandBarPreview({ openAt = { x: 360, y: 220 }, preset = 'basic', mode = 'pane' }: Props) {
  const [selected, setSelected] = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    seedPalette(preset);
    const rect = containerRef.current?.getBoundingClientRect();
    const vx = (rect?.left ?? 0) + openAt.x + window.scrollX;
    const vy = (rect?.top ?? 0) + openAt.y + window.scrollY;
    useCanvasStore.getState().openCommandBarAt(vx, vy, { mode });
    return () => {
      useCanvasStore.getState().closeCommandBar();
    };
  }, [openAt.x, openAt.y, preset, mode]);
  return (
    <div ref={containerRef} className="h-[560px] border rounded relative">
      <div className="absolute top-2 left-2 text-xs text-muted-foreground">
        Try typing to filter; arrow up/down; Enter to select.
      </div>
      <CommandBar onSelect={(tpl) => setSelected(`${tpl.key} • ${tpl.title}`)} />
      <div className="absolute bottom-2 left-2 text-xs">
        Selected: <span className="font-mono">{selected ?? '—'}</span>
      </div>
    </div>
  );
}

const meta: StoryMeta<Props> = {
  id: 'flows/CommandBar',
  title: 'CommandBar',
  group: 'Flows/Palette',
  component: CommandBarPreview as any,
  description: 'Command Palette: Packages → Actions with search and keyboard.',
  defaultProps: { openAt: { x: 360, y: 220 }, preset: 'basic', mode: 'pane' },
  controls: {
    preset: { type: 'select', label: 'Preset', options: ['basic', 'mixed'] },
    openAt: { type: 'json', label: 'Anchor' },
    mode: { type: 'select', label: 'Context', options: ['pane', 'connect'] },
  },
  variants: [
    { name: 'Packages → Actions', props: { preset: 'basic' } },
    { name: 'Mixed Types', props: { preset: 'mixed', openAt: { x: 420, y: 180 } } },
    { name: 'Connect Context', props: { mode: 'connect' } },
  ],
};

export default meta;
